import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useAppSelector } from '@/hooks/reduxHooks';
import {
  useListAssessmentTranslationsQuery,
  useTranslateAssessmentDocumentsMutation,
  type DocumentTranslation,
  type RenderMode,
} from '@/pages/application/apis/applicationApi';

const POLL_MS = 3000;

const joinUrl = (base: string, path: string) =>
  `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

const isInFlight = (t: DocumentTranslation) =>
  t.status === 'QUEUED' || t.status === 'RUNNING';

/**
 * Loads the persisted document-translation denotions for an assessment and
 * exposes them indexed by `documentId` for overlaying onto the document list.
 *
 * Polls the list every {@link POLL_MS} while any row is still QUEUED/RUNNING and
 * stops automatically once everything is terminal (COMPLETED/FAILED) — so rows
 * flip to their final badge as each document finishes server-side.
 */
export const useAssessmentTranslations = (assessmentId?: string) => {
  // Polling toggles off once nothing is in flight. Seeded on so a freshly
  // kicked-off run starts polling immediately after the list refetches.
  const [pollingInterval, setPollingInterval] = useState(POLL_MS);

  const { data: translations = [], isFetching } =
    useListAssessmentTranslationsQuery(assessmentId!, {
      skip: !assessmentId,
      pollingInterval,
      skipPollingIfUnfocused: true,
    });

  const anyInFlight = useMemo(
    () => translations.some(isInFlight),
    [translations],
  );

  // Poll while work is outstanding; stop when everything is terminal.
  useEffect(() => {
    setPollingInterval(anyInFlight ? POLL_MS : 0);
  }, [anyInFlight]);

  const byDoc = useMemo(
    () => new Map(translations.map((t) => [t.documentId, t])),
    [translations],
  );

  const [translate, { isLoading: isTranslating }] =
    useTranslateAssessmentDocumentsMutation();

  // The translated PDF lives behind an auth-guarded, non-expiring app route, so
  // a bare <a href> 401s. Fetch it with the bearer token and hand the browser a
  // blob object URL instead. `openingDocId` drives a per-row loading state.
  const token = useAppSelector((s) => s.auth.token);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);

  // Fetch the auth-guarded translated PDF and return a blob object URL for
  // embedding (iframe/object) or opening. The CALLER owns revocation
  // (URL.revokeObjectURL) once it's done with the URL.
  const fetchTranslatedObjectUrl = useCallback(
    async (translation: DocumentTranslation): Promise<string | null> => {
      if (!translation.translatedPdfUrl) return null;
      const base = import.meta.env.VITE_BACKEND_URL ?? '';
      const res = await fetch(joinUrl(base, translation.translatedPdfUrl), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    [token],
  );

  const openTranslatedPdf = useCallback(
    async (translation: DocumentTranslation) => {
      if (!translation.translatedPdfUrl) return;
      // Open the tab synchronously inside the click gesture so popup blockers
      // don't swallow it; navigate it to the blob once the bytes arrive.
      const win = window.open('', '_blank');
      setOpeningDocId(translation.documentId);
      try {
        const objectUrl = await fetchTranslatedObjectUrl(translation);
        if (!objectUrl) throw new Error('No translated PDF');
        if (win) win.location.href = objectUrl;
        else window.open(objectUrl, '_blank');
        // Give the new tab time to load before releasing the blob.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch {
        win?.close();
        message.error('Failed to open translated PDF');
      } finally {
        setOpeningDocId(null);
      }
    },
    [fetchTranslatedObjectUrl],
  );

  const translateDocuments = useCallback(
    async (documentIds: string[], mode?: RenderMode) => {
      if (!assessmentId || documentIds.length === 0) return false;
      try {
        await translate({ assessmentId, documentIds, mode }).unwrap();
        // Rows come back QUEUED via the invalidated list query; make sure the
        // poller is running to watch them progress.
        setPollingInterval(POLL_MS);
        message.success(
          `Translating ${documentIds.length} document${
            documentIds.length === 1 ? '' : 's'
          }…`,
        );
        return true;
      } catch {
        message.error('Failed to start translation');
        return false;
      }
    },
    [assessmentId, translate],
  );

  return {
    translations,
    byDoc,
    anyInFlight,
    isFetching,
    isTranslating,
    translateDocuments,
    openTranslatedPdf,
    fetchTranslatedObjectUrl,
    openingDocId,
  };
};
